// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : { body };

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    // headers: headers || { 'Content-Type': 'application/transit+json' },
    headers: {
      ...(!!headers
        ? Object.keys(headers).length > 1
          ? headers
          : {}
        : { 'Content-Type': 'application/transit+json' }),
    },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

export const cartTransactionLineItems = body => {
  return post('/api/cart-transaction-line-items', body);
};
// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};
export const deleteClodinaryImage = body => {
  return post('/api/deleteImage', body);
};

export const getCircleAuthToken = body => {
  return post('/api/circle-auth-token', body);
};

export const checkCircleMemberExists = body => {
  return post('/api/check-circle-member-exists', body);
};

export const getAuthToken = body => {
  return;
  // return post('/api/create-auth-token', body);
};
export const getCircleHomePageApi = body => {
  return post('/api/get-circle-homePage', body);
};

export const createSpacePostApi = body => {
  return post('/api/create-space-post', body);
};

export const createLikeForPostApi = body => {
  return post('/api/create-like-for-post', body);
};

export const createDislikeForPostApi = body => {
  return post('/api/create-dislike-for-post', body);
};

export const createCommentForPostApi = body => {
  return post('/api/create-comment-for-post', body);
};

export const deleteCommentFromPostApi = body => {
  return post('/api/delete-comment-from-post', body);
};

export const fetchCurrentCommunityMember = body => {
  return post('/api/get-current-community-member', body);
};

export const fetchSpaceListApi = body => {
  return post('/api/get-list-of-spaces', body);
};
export const fetchSpaceListApiV2 = body => {
  return post('/api/get-list-of-spaces-v2', body);
};

export const redirectToCircleApi = body => {
  return post('/api/redirectToCircle', body);
};

export const createNewCircleUserApi = body => {
  return post('/api/create-new-circle-user', body);
};

export const directUploadApi = body => {
  return post('/api/direct-upload', body);
};

export const createNewSpaceApi = body => {
  return post('/api/create-new-space', body);
};

export const joinSpaceByIdApi = body => {
  return post('/api/join-space-by-id', body);
};

export const trendingTopicsApi = body => {
  return post('/api/trending-topics', body);
};

export const leaveSpaceByIdApi = body => {
  return post('/api/leave-space-by-id', body);
};

export const createBookMarkApi = body => {
  return post('/api/create-bookmark', body);
};

export const deleteBookMarkApi = body => {
  return post('/api/delete-bookmark', body);
};

export const fetchBookMarkApi = body => {
  return post('/api/fetch-bookmark', body);
};

export const getSinglePostApi = body => {
  return post('/api/get-single-post', body);
};

export const getCommentListForPostApi = body => {
  return post('/api/get-comment-list-for-post', body);
};

export const createLikeDislikeCommentApi = body => {
  return post('/api/create-like-dislike-comment', body);
};

export const getListOfReplyForCommentApi = body => {
  return post('/api/get-list-of-reply-for-comment', body);
};

export const createReplyForACommentApi = body => {
  return post('/api/create-reply-for-a-comment', body);
};

export const getSubscriptionPrice = body => {
  return post('/api/get-subscription-price', body);
};

export const createStripeSubscription = body => {
  return post('/api/stripe-create-subscription', body);
};

export const getStripeSubscriptionStatus = body => {
  return post('/api/stripe-user-subscription-status', body);
};

export const onCancelStripeSubscription = body => {
  return post('/api/cancel-stripe-subscription', body);
};
export const getSpaceDetailsApi = body => {
  return post('/api/get-space-details', body);
};

export const getSpaceTopicDetailApi = body => {
  return post('/api/get-space-topic-detail', body);
};

export const getSpaceMembersApi = body => {
  return post('/api/get-space-members', body);
};

export const removeSpaceMemberApi = body => {
  return post('/api/remove-space-member', body);
};

export const deleteSpaceApi = body => {
  return post('/api/delete-space', body);
};

export const getBookMarksApi = body => {
  return post('/api/get-bookmarks', body);
};

export const uploadToR2 = body => {
  const options = {
    headers: {
      // 'Content-Type': 'multipart/form-data',
      // browser should set this header automatically when using FormData. Manually setting this header can cause the "Multipart: Boundary not found" error.
    },
  };
  return post('/api/cloudfare/upload-file-to-r2', body, options);
};

export const updateCurrentCircleUserApi = body => {
  return post('/api/update-current-circle-user', body);
};

export const advancedSearchApi = body => {
  return post('/api/advanced-search', body);
};

export const editCommentApi = body => {
  return post('/api/edit-comment-for-post', body);
};

export const editCirclePostApi = body => {
  return post('/api/edit-circle-post', body);
};

export const deleteCirclePostApi = body => {
  return post('/api/delete-circle-post', body);
};

export const followCirclePostApi = body => {
  return post('/api/follow-circle-post', body);
};

export const flaggedContentApi = body => {
  return post('/api/flagged-content', body);
};

export const getSpaceTopicsApi = body => {
  return post('/api/get-space-topics', body);
};

export const searchGroupsApi = body => {
  return post('/api/search-groups', body);
};

export const searchSpaceGroupsApi = body => {
  return post('/api/search-space-groups', body);
};

export const getNotificationsListApi = body => {
  return post('/api/get-notifications-list', body);
};

export const getNotificationsCountApi = body => {
  return post('/api/get-new-notification-counts', body);
}

export const markNotificationAsReadApi = body => {
  return post('/api/mark-notifications-as-read', body);
}

export const resetNotificationCountApi = body => {
  return post('/api/reset-notification-count', body);
}

export const notificationPreferencesApi = body => {
  return post('/api/notification-preferences', body);
}

export const getNotificationPrefernceApi = body => {
  return post('/api/get-notification-preferences', body)
}

export const getMemberDetailApi = body => {
  return post('/api/get-member-detail', body);
}

export const getMediaTranscodingStatusApi = body => {
  return post('/api/get-media-transcoding-status', body);
}

export const markAllNotificationsAsReadApi = body => {
  return post('/api/mark-all-notifications-as-read', body);
}

export const addMemberToSpaceApi = body => {
  return post('/api/add-member-to-space', body)
}

export const findMemberToSpaceApi = body => {
  return post('/api/find-member-to-space', body)
}


// send grid apis
export const onSingleMailApiCollection = body => {
  return post('/api/singleMailApiCollection', body);
};

export const onMultiMailApiCollection = body => {
  return post('/api/multiMailApiCollection', body);
};

export const updateUserApi = body => post('/api/updateUser', body);


// mongo fetch data
export const onUpdateCategoryCollection = body => {
  return post('/api/updateCategoryCollection', body);
};

export const onFindCategoryCollection = (body) => {
  return post('/api/findCategoryCollection', body);
};
export const onGroupJoinRequestCollection = (body) => {
  return post('/api/groupJoinRequestCollection', body);
};
export const onGetGroupJoinRequest = (body) => {
  return post('/api/getGroupJoinRequest', body);
};
export const onDeclineGroupJoinRequest = body => {
  return post('/api/declineGroupJoinRequest', body)
}

export const groupPostSearchApi = body => {
  return post('/api/group-post-search', body)
}

export const fetchUserActivityApi = body => {
  return post('/api/fetch-user-activity', body)
}

// <<<<<<<< algolia apiType check in types.js >>>>>>>>>>>>>>>>
export const algoliaCRUDApis = (apiType, body) => {
  return post(`/api/${apiType}`, body);
};

// shippo apis

export const getShippingRates = (body) => {
  return post('/api/fetchShippingRates', body);
};

export const createShippingLabel = (body) => {
  return post('/api/createShippingLabel', body);
};

export const shippoWebhook = (body) => {
  return post('/api/trackShippoOrder', body);
};

export const validatingAddressWithShippo = (body) => {
  return post('/api/validatingAddress', body);
};

export const getCarrierAccount = (body) => {
  return post('/api/getCarriers', body);
};


// create folder for groups
export const onGroupFolderCollectionCreate = (body) => {
  return post('/api/groupFolderCollectionCreate', body);
};
export const onGroupFolderCollectionFind = (body) => {
  return post('/api/groupFolderCollectionFind', body);
};
export const onGroupFolderCollectionUpdate = (body) => {
  return post('/api/groupFolderCollectionUpdate', body);
};

// create documnets for folders
export const onGroupFolderDocumentCollectionCreate = (body) => {
  return post('/api/groupFolderDocumentCollectionCreate', body);
};
export const onGroupFolderDocumentCollectionFind = (body) => {
  return post('/api/groupFolderDocumentCollectionFind', body);
};
export const onGroupFolderDocumentCollectionUpdate = (body) => {
  return post('/api/groupFolderDocumentCollectionUpdate', body);
};
// integration apis

export const updateYouthAccount = (body) => {
  return post('/api/updateYouthAccount', body);
};
// fetch user data
export const fetchUser = (body) => {
  return post('/api/fetchUserData', body);
};
// organizationCollectionFind
export const onOrganizationCollectionFind = (body) => {
  return post('/api/organizationCollectionFind', body);
};

// open ai apis

export const getSpecificUserGPTS = (body) => {
  return post('/api/fetch-users-gpts', body);
};

export const chatUserWithGPTS = (body) => {
  return post('/api/chat-with-gpts', body);
};

export const fetchAllGpts = (body) => {
  return post('/api/fetch-all-gpts', body);
};

export const fetchChatHistory = (body) => {
  return post('/api/fetch-chat-history', body);
};
export const deleteRulebookApi = (body) => {
  return post('/api/delete-rule-book', body)
};
export const addOrRemoveFavouriteGpt = (body) => {
  return post('/api/add-remove-fav-gpt', body)
};
export const getFavouriteGpt = (body) => {
  return post('/api/get-fav-gpt', body)
};
export const fetchAllGptsWithPage = (body) => {
  return post('/api/get-all-gpts-page', body)
};

export const addNewRulebook = (body) => {
  return post('/api/add-rule-book', body);
};

export const updateExistingRulebook = (body) => {
  return post('/api/update-rule-book', body);
};

export const deleteRuleBookFile = (body) => {
  return post('/api/delete-rule-book-file', body);
};

export const getAllRulebooks = (body) => {
  return post('/api/get-all-rulebooks', body);
};

export const fetchListOfSpacesbyEmail = (body) => {
  return post('/api/fetch-group-list', body);
};
